import { bodyHealthId } from '../../../supabase/functions/_shared/bodyHealthActionDomain';
import type { HealthProvider } from './healthMap';

export interface RecentHealthImportReply { data: unknown; error: unknown; status?: number }
export interface RecentHealthImportQuery extends PromiseLike<RecentHealthImportReply> {
  select(columns: string): RecentHealthImportQuery;
  abortSignal(signal: AbortSignal): RecentHealthImportQuery;
  setHeader(name: string, value: string): RecentHealthImportQuery;
}
// Keep the SDK's recursive database types at the transport boundary.
export interface RecentHealthImportOptions {
  client: { rpc: unknown };
  ownerId: string; connectionId: string; provider: HealthProvider; bearer: string;
  signal: AbortSignal; assertCurrent: () => void; recentRunId?: string;
}
export interface RecentHealthImportClient {
  rpc(name: string, args: Record<string, unknown>): RecentHealthImportQuery;
}
interface RecentRpcQuery extends PromiseLike<unknown> {
  abortSignal(signal: AbortSignal): RecentRpcQuery;
  setHeader(name: string, value: string): RecentRpcQuery;
}
type RecentRpc = (name: 'import_native_health_recent_v1', args: Record<string, unknown>) => RecentRpcQuery;
const METRIC_COLUMNS = 'id,user_id,source,external_id,metric_type,value,unit,recorded_at,ended_at,metadata';
const ENVELOPE_FIELDS = ['ok', 'version', 'user_id', 'connection_id', 'provider', 'table', 'data'];
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object'
  && !Array.isArray(value) && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
const own = (value: object, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);
const keys = (value: Record<string, unknown>, fields: string[]): boolean => Object.keys(value).length === fields.length
  && fields.every(field => own(value, field));
const fail = (reason: string): never => { throw new Error('health_recent_import_' + reason); };
function json(value: unknown, ancestors = new Set<object>()): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (!Array.isArray(value) && !object(value) || ancestors.has(value as object)
    || Array.isArray(value) && (Object.keys(value).length !== value.length
      || Object.keys(value).some((key, index) => key !== String(index)))) return false;
  ancestors.add(value as object);
  const valid = Object.values(value).every(item => json(item, ancestors));
  ancestors.delete(value as object);
  return valid;
}
function freeze<T>(value: T, seen = new Set<object>()): T {
  if (value !== null && typeof value === 'object' && !seen.has(value)) {
    seen.add(value);
    Object.values(value).forEach(item => freeze(item, seen));
    Object.freeze(value);
  }
  return value;
}
function capturedRows(input: Record<string, unknown>, owner: string, provider: HealthProvider): Record<string, unknown>[] {
  let args: unknown;
  try { args = structuredClone(input); } catch { return fail('invalid'); }
  if (!object(args) || !keys(args, ['p_records']) || !Array.isArray(args.p_records)
    || args.p_records.length > 100 || !json(args.p_records)) return fail('invalid');
  for (const row of args.p_records) {
    if (!object(row) || !own(row, 'user_id') || !own(row, 'source') || row.source !== provider) return fail('invalid');
    try { if (bodyHealthId(row.user_id) !== owner) return fail('invalid'); } catch { return fail('invalid'); }
    row.user_id = owner;
  }
  return freeze(args.p_records as Record<string, unknown>[]);
}

/** Bind recent Sleep and metric imports to one captured active connection.
 * The existing helpers still validate domain payloads and count confirmations.
 * Their legacy RPC names are accepted only here; every actual request goes to
 * the connection-authorized wrapper, without a REST or replay fallback.
 */
export function createRecentHealthImportClient(options: RecentHealthImportOptions): RecentHealthImportClient {
  // Capture context before any caller callback or transport code can run.
  const { client, ownerId, connectionId, provider, bearer, signal, assertCurrent, recentRunId } = options;
  let owner: string, connection: string;
  try { owner = bodyHealthId(ownerId); connection = bodyHealthId(connectionId); } catch { return fail('invalid'); }
  if (!['apple_health', 'health_connect'].includes(provider) || typeof bearer !== 'string' || !bearer
    || Array.from(bearer).some(character => /\s/.test(character) || character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
    || !signal || typeof signal.aborted !== 'boolean' || typeof assertCurrent !== 'function'
    || !client || typeof client.rpc !== 'function') return fail('invalid');
  const rpc = client.rpc as RecentRpc, authorization = 'Bearer ' + bearer;
  const active = () => {
    assertCurrent();
    if (signal.aborted) throw new DOMException('Recent health import aborted', 'AbortError');
  };
  return Object.freeze({
    rpc(name: string, input: Record<string, unknown>): RecentHealthImportQuery {
      const table = name === 'import_sleep_records' ? 'sleep_logs'
        : name === 'import_native_health_metrics_v1' ? 'health_metrics' : fail('invalid');
      // Freeze this request before a guard, an await, or the SDK can mutate it.
      const rows = capturedRows(input, owner, provider);
      const args = freeze({ p_connection_id: connection, p_provider: provider, p_table: table, p_records: rows, ...(recentRunId ? { p_recent_run_id: bodyHealthId(recentRunId) } : {}) });
      let pending: Promise<RecentHealthImportReply> | undefined;
      const execute = async (): Promise<RecentHealthImportReply> => {
        let reply: unknown;
        try {
          active();
          const raw = await rpc.call(client, 'import_native_health_recent_v1', args)
            .abortSignal(signal).setHeader('Authorization', authorization);
          if (!object(raw)) return fail('unconfirmed');
          // Capture the returned body before the post-await guard can run.
          try { reply = freeze(structuredClone(raw)); } catch { return fail('unconfirmed'); }
        } finally { active(); }
        if (!object(reply)) return fail('unconfirmed');
        // Preserve SQL errors and their real HTTP status. Only the existing
        // metric helper recognizes the exact rolled-back protection rejection.
        if (reply.error !== null) {
          if (reply.error === undefined) return fail('unconfirmed');
          return reply as unknown as RecentHealthImportReply;
        }
        const body = reply.data;
        if (!Number.isInteger(reply.status) || (reply.status as number) < 200 || (reply.status as number) >= 300
          || !object(body) || !keys(body, ENVELOPE_FIELDS) || !json(body)
          || body.ok !== true || body.version !== 1 || body.user_id !== owner || body.connection_id !== connection
          || body.provider !== provider || body.table !== table) return fail('unconfirmed');
        return Object.freeze({ data: body.data, error: null, status: reply.status as number });
      };
      const query: RecentHealthImportQuery = Object.freeze({
        select(columns: string) {
          // The wrapper returns JSON. Forwarding the legacy metric projection
          // would project that scalar RPC instead of its inner metric records.
          if (table !== 'health_metrics' || columns !== METRIC_COLUMNS) return fail('invalid');
          return query;
        },
        abortSignal(value: AbortSignal) {
          if (value !== signal) return fail('invalid');
          return query;
        },
        setHeader(name: string, value: string) {
          if (typeof name !== 'string' || name.toLowerCase() !== 'authorization' || value !== authorization) return fail('invalid');
          return query;
        },
        then<TResult1 = RecentHealthImportReply, TResult2 = never>(
          onfulfilled?: ((value: RecentHealthImportReply) => TResult1 | PromiseLike<TResult1>) | null,
          onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
        ): PromiseLike<TResult1 | TResult2> {
          const started = pending !== undefined;
          // Install the shared promise before execute can call a guard or the
          // SDK. Either may observe this same query reentrantly.
          pending ??= Promise.resolve().then(execute);
          // Awaiting the same query again never sends a second mutation, but
          // must still reject if this run has since become stale or aborted.
          return (started ? pending.finally(active) : pending).then(onfulfilled, onrejected);
        },
      });
      return query;
    },
  });
}
